import { CheckCircle } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { notificationDestination, notificationSentenceKey } from "~/api/notificationEvents";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "~/api/queries";
import type { Notification } from "~/api/types";
import { Avatar } from "~/foundation/components/Avatar";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { formatRelativeTime } from "~/foundation/utils/time";

/**
 * W9 §A.5.2 — the bell's inbox, derived clean-room from `NotificationsController`,
 * `ApiPresenters.PresentNotification` and `SignalRRealtimeNotificationService` (no Outline
 * notification component was read — §D.3). Lives in `features/notifications/`, not
 * `shell/NotificationInbox.tsx` as the wave record first sketched it: `slate/src/shell/**` is
 * off limits to this builder except `ConsoleBar/StatusCluster.tsx`, which mounts this
 * component additively instead.
 */

function Row({
  notification,
  onOpen,
}: {
  notification: Notification;
  onOpen: (n: Notification) => void;
}) {
  const { t, i18n } = useTranslation();
  const unread = notification.viewedAt === null;

  return (
    <li className="border-b border-divider last:border-b-0">
      <button
        type="button"
        onClick={() => onOpen(notification)}
        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-start hover:bg-hover-bg"
      >
        <Avatar
          name={notification.actor?.name ?? "?"}
          src={notification.actor?.avatarUrl ?? null}
          size={28}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-body text-text">
            {t(notificationSentenceKey(notification.event), {
              actor: notification.actor?.name ?? t("Someone"),
            })}
          </span>
          {notification.document?.title ? (
            <span className="block truncate text-small text-text-secondary">
              {notification.document.title}
            </span>
          ) : null}
          <span className="block text-small text-text-tertiary">
            {formatRelativeTime(notification.createdAt, i18n.language)}
          </span>
        </span>
        {unread ? (
          <span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" />
        ) : null}
      </button>
    </li>
  );
}

export function NotificationInbox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications({ archived: false, limit: 20, popoverOpen: true });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.notifications ?? [];

  const open = (notification: Notification) => {
    if (notification.viewedAt === null) {
      markRead.mutate(notification.id);
    }
    const destination = notificationDestination(notification.event, notification.documentId);
    if (destination) {
      void navigate(destination as never);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Spinner label={t("Loading")} />
      </div>
    );
  }

  if (notifications.length === 0) {
    // N13 — the same empty state slate already rendered before this wave.
    return <Empty message={t("You're all caught up")} />;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-1">
        <span className="text-small font-medium text-text-secondary">{t("Notifications")}</span>
        <Button
          size="sm"
          variant="ghost"
          icon={CheckCircle}
          disabled={markAllRead.isPending || !notifications.some((n) => n.viewedAt === null)}
          onClick={() => markAllRead.mutate()}
        >
          {t("Mark all read")}
        </Button>
      </div>
      <ul className="flex max-h-[380px] flex-col overflow-y-auto rounded-md border border-divider">
        {notifications.map((notification) => (
          <Row key={notification.id} notification={notification} onOpen={open} />
        ))}
      </ul>
    </div>
  );
}
